import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaPlus, FaTimes, FaTrash, FaStar, FaEdit, FaUndo, FaChevronRight, FaEyeSlash, FaGripVertical, FaArrowUp, FaArrowDown, FaClipboardList, FaHeading, FaMobileAlt } from 'react-icons/fa';
import './css_pages/Preps.css';
import { getAllPreps, createPrep, updatePrep, deletePrep, getPrepById } from '../services/prepsService';
import { getProductsByStoreId } from '../services/productsByStoreService';
import { useStore } from '../contexts/StoreContext';
import { useAuth } from '../contexts/AuthContext';

const ModalPortal = ({ children }) => ReactDOM.createPortal(children, document.body);

function Preps() {
  const { selectedStore } = useStore();
  const { isKitchen } = useAuth();
  const kitchenMode = isKitchen();   // true => modo cocinero (solo ver y escalar)

  const [preps, setPreps]       = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // ── Modales ──
  const [showModal, setShowModal]               = useState(false);
  const [showDeleteModal, setShowDeleteModal]   = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showPrepModal, setShowPrepModal]       = useState(false);
  const [showDetailModal, setShowDetailModal]   = useState(false);

  const [deleteId, setDeleteId]   = useState(null);
  const [editingId, setEditingId] = useState(null);

  // ── Detail state ──
  const [detailPrep, setDetailPrep] = useState(null);
  const [mainQty, setMainQty]       = useState('');

  // ── Form state: UNA sola lista "items" (product | section | prep) ──
  const [formData, setFormData] = useState({
    name: '', items: [], yieldQuantity: '', yieldUnit: '',
    showInPhysicalInventory: true, instructions: '', toPrepareQty: ''
  });
  const [customYieldUnit, setCustomYieldUnit] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [prepSearch, setPrepSearch]       = useState('');

  // ── Drag & drop reorder state (una sola lista unificada) ──
  const [dragIndex, setDragIndex]         = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    if (selectedStore) { fetchPreps(); fetchProducts(); }
  }, [selectedStore]);

  // ── Fetch ──────────────────────────────────────────────────────────────
  const fetchPreps = async () => {
    if (!selectedStore) return;
    try {
      setLoading(true); setError(null);
      const r = await getAllPreps(selectedStore.id);
      if (r.success) setPreps(r.data);
    } catch { setError('Error loading preparations'); }
    finally { setLoading(false); }
  };

  const fetchProducts = async () => {
    if (!selectedStore) return;
    try {
      const r = await getProductsByStoreId(selectedStore.id);
      if (r.success) setProducts(r.data.map(item => ({
        id: item.productId, name: item.productName, productCode: item.productCode,
        containerSize: item.containerSize, containerUnit: item.containerUnit,
        containerType: item.containerType, categoryName: item.categoryName || '',
        wholesalePrice: item.wholesalePrice || 0, caseSize: item.caseSize || 1
      })));
    } catch {}
  };

  // ── Unit helpers ────────────────────────────────────────────────────────
  const VOLUME_TO_ML = { 'ml':1,'L':1000,'L (liter)':1000,'Liter':1000,'Gallon':3785.41,'fl oz':29.5735,'fl oz (fluid ounce)':29.5735,'Ounce':29.5735 };
  const WEIGHT_TO_G  = { 'g':1,'g (gram)':1,'kg':1000,'kg (kilogram)':1000,'lb':453.592,'lb (pound)':453.592,'oz':28.3495,'oz (ounce)':28.3495 };
  const getUnitType   = (unit) => { if (VOLUME_TO_ML[unit]) return 'volume'; if (WEIGHT_TO_G[unit]) return 'weight'; return 'count'; };
  const convertToBase = (qty, unit, baseUnit) => {
    if (unit === baseUnit) return qty;
    if (VOLUME_TO_ML[unit] && VOLUME_TO_ML[baseUnit]) return (qty * VOLUME_TO_ML[unit]) / VOLUME_TO_ML[baseUnit];
    if (WEIGHT_TO_G[unit]  && WEIGHT_TO_G[baseUnit])  return (qty * WEIGHT_TO_G[unit])  / WEIGHT_TO_G[baseUnit];
    return qty;
  };
  const getUnitPrice   = (p) => { const price = parseFloat(p.wholesalePrice)||0; const cs = parseFloat(p.caseSize)||1; return cs > 0 ? price/cs : price; };
  const calcIngCost    = (qty, unit, prodSize, prodUnit, unitPrice) => {
    if (!unitPrice || unitPrice <= 0 || !qty || qty <= 0) return 0;
    if (getUnitType(prodUnit) === 'count') return parseFloat(qty) * unitPrice;
    return (unitPrice / convertToBase(parseFloat(prodSize)||1, prodUnit, prodUnit)) * convertToBase(parseFloat(qty), unit, prodUnit);
  };
  const calcPrepCostFn = (prep, qty, selUnit) => {
    const bc = parseFloat(prep.prepCost)||0, bq = parseFloat(prep.baseQuantity)||1, bu = prep.baseUnit||'Each';
    if (!qty || parseFloat(qty) <= 0) return '0.00';
    const cpbu = bc / bq;
    if (selUnit === bu) return (cpbu * parseFloat(qty)).toFixed(2);
    if (selUnit === 'Each' || bu === 'Each') return (bc * parseFloat(qty)).toFixed(2);
    const all = { ...VOLUME_TO_ML, ...WEIGHT_TO_G };
    if (all[selUnit] && all[bu]) return (cpbu * (parseFloat(qty) * all[selUnit]) / all[bu]).toFixed(2);
    return (cpbu * parseFloat(qty)).toFixed(2);
  };

  // ── Totales (derivados de la lista unificada) ──
  const totalIngCost = () => formData.items.filter(i => i.itemType === 'product').reduce((s,i) => s + parseFloat(i.totalCost||0), 0).toFixed(2);
  const totalSubCost = () => formData.items.filter(i => i.itemType === 'prep').reduce((s,i) => s + parseFloat(i.totalCost||0), 0).toFixed(2);
  const totalCost    = () => (parseFloat(totalIngCost()) + parseFloat(totalSubCost())).toFixed(2);

  const realItemsCount = () => formData.items.filter(i => i.itemType === 'product' || i.itemType === 'prep').length;

  // ── Normalizar la respuesta del detalle a una lista unificada ──
  // Si el backend nuevo manda "items", se usa directo. Si es un
  // backend viejo (solo ingredients + subPreps), se reconstruye.
  const normalizeItems = (d) => {
    if (Array.isArray(d.items) && d.items.length > 0) return d.items;
    return [
      ...(d.ingredients || []),
      ...((d.subPreps || []).map(sp => ({ ...sp, itemType: 'prep' })))
    ];
  };

  // ── Detail modal ────────────────────────────────────────────────────────
  const handleOpenDetail = async (prep) => {
    try {
      setLoading(true);
      const r = await getPrepById(prep.id);
      if (r.success) {
        const items = normalizeItems(r.data);
        setDetailPrep({ ...r.data, items });
        const main = items.find(i => i.isMain === 1 || i.isMain === true);
        const savedTP = r.data.toPrepareQty;
        // Distinguir 0 REAL (mostrar 0 y obligar a capturar) de vacío/null (usar original)
        const hasTP = savedTP !== null && savedTP !== undefined && savedTP !== '' && !isNaN(parseFloat(savedTP));
        setMainQty(hasTP ? String(parseFloat(savedTP)) : (main ? String(main.quantity) : ''));
        setShowDetailModal(true);
      }
    } catch { alert('Error loading preparation'); }
    finally { setLoading(false); }
  };

  const handleCloseDetail = () => { setShowDetailModal(false); setDetailPrep(null); setMainQty(''); };

  // ── Escala ──────────────────────────────────────────────────────────────
  const detailItems    = detailPrep ? (detailPrep.items || []) : [];
  const detailMainItem = detailItems.find(i => i.isMain === 1 || i.isMain === true) || null;
  const origQty    = detailMainItem ? parseFloat(detailMainItem.quantity) : null;
  const currentQty = !isNaN(parseFloat(mainQty)) ? parseFloat(mainQty) : (origQty || 1);
  const scale      = origQty && origQty > 0 ? currentQty / origQty : 1;
  const isScaled   = Math.abs(scale - 1) > 0.0001;

  const scaledQty = (originalQty, isMainItem) => {
    if (isMainItem) return currentQty;
    return parseFloat((parseFloat(originalQty) * scale).toFixed(4));
  };

  // ── Print vía RawBT (Android) ────────────────────────────────────────────
  // Manda la receta como TEXTO PLANO por el esquema intent: de RawBT.
  // RawBT la imprime en el modo texto nativo de la impresora (sin
  // rasterizar la pantalla), evitando el garabato que produce
  // window.print() sobre rollo térmico en móvil.
  // Ancho del rollo: 32 chars ≈ 58mm · 48 chars ≈ 80mm.
  // Si el ticket sale corrido/cortado, ajustar RAWBT_WIDTH al ancho real.
  const RAWBT_WIDTH = 32;

  const buildTicketText = () => {
    const LF   = '\n';
    const W    = RAWBT_WIDTH;
    const sep  = '='.repeat(W);
    const dash = '-'.repeat(W);

    // "nombre .......... cantidad unidad" alineado a la derecha.
    // Si la línea no cabe en el ancho, se deja "nombre cantidad"
    // en una sola línea y la impresora la ajusta sola.
    const row = (left, right) => {
      const l = String(left), r = String(right);
      const gap = W - l.length - r.length;
      return gap > 0 ? l + ' '.repeat(gap) + r : l + ' ' + r;
    };

    let t = '';
    t += (detailPrep.name || 'Pre-Batch').toUpperCase() + LF;
    if (isScaled) t += `(scaled ${scale.toFixed(2)}x)` + LF;
    t += sep + LF;

    detailItems.forEach(it => {
      if (it.itemType === 'section') {
        const label = (it.sectionLabel || '').trim();
        if (label) t += LF + `-- ${label.toUpperCase()} --` + LF;
        return;
      }
      const isMain = it.isMain === 1 || it.isMain === true;
      const qty    = scaledQty(parseFloat(it.quantity), isMain);
      const name   = (isMain ? '* ' : '') + (it.itemType === 'prep' ? it.prepName : it.productName);
      t += row(name, `${qty} ${it.unit || ''}`.trim()) + LF;
    });

    if (detailPrep.instructions && detailPrep.instructions.trim()) {
      t += LF + dash + LF + 'INSTRUCTIONS' + LF + detailPrep.instructions.trim() + LF;
    }

    // avance de papel para poder cortar
    return t + LF + LF + LF;
  };

  const handlePrintRawBT = () => {
    // encodeURI, tal como recomienda la documentación oficial de RawBT.
    const textEncoded = encodeURI(buildTicketText());
    const S = '#Intent;scheme=rawbt;';
    const P = 'package=ru.a402d.rawbtprinter;end;';
    window.location.href = 'intent:' + textEncoded + S + P;
  };

  // ── Main item toggle (products y preps; sections nunca) ──────────────────
  const handleSetMain = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((it, i) => {
        if (it.itemType === 'section') return it;
        return { ...it, isMain: i === index ? !it.isMain : false };
      })
    }));
  };

  // ── Form ────────────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormData({ name:'', items:[], yieldQuantity:'', yieldUnit:'', showInPhysicalInventory: true, instructions: '', toPrepareQty: '' });
    setCustomYieldUnit('');
  };
  const handleOpenModal  = () => { if (kitchenMode) return; resetForm(); setEditingId(null); setShowModal(true); };
  const handleCloseModal = () => { setShowModal(false); resetForm(); setEditingId(null); };
  const handleInput = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleEditModal = async (prep) => {
    if (kitchenMode) return;
    try {
      setLoading(true);
      const r = await getPrepById(prep.id || prep);
      if (r.success) {
        const d = r.data;
        const rawItems = normalizeItems(d);
        const KNOWN_UNITS = ['', 'ml', 'L', 'Gallon', 'fl oz', 'g', 'kg', 'lb', 'oz', 'Each'];
        const loadedUnit  = d.yieldUnit || '';
        const isCustom    = loadedUnit !== '' && !KNOWN_UNITS.includes(loadedUnit);

        const items = rawItems.map(it => {
          if (it.itemType === 'section') {
            return { itemType: 'section', sectionLabel: it.sectionLabel || '' };
          }
          if (it.itemType === 'prep') {
            return {
              itemType: 'prep',
              prepId: it.prepId, prepName: it.prepName, prepCost: parseFloat(it.prepCost)||0,
              baseQuantity: parseFloat(it.baseQuantity)||1, baseUnit: it.baseUnit||'Each',
              quantity: it.quantity||1, unit: it.unit||it.baseUnit||'Each',
              unitType: getUnitType(it.baseUnit||'Each'), totalCost: parseFloat(it.totalCost)||0,
              isMain: it.isMain === 1 || it.isMain === true
            };
          }
          // product
          const found = products.find(p => p.id === it.productId);
          const up    = found ? getUnitPrice(found) : 0;
          return {
            itemType: 'product',
            productId: it.productId, productName: it.productName,
            productSize: found?.containerSize||1, productUnit: found?.containerUnit||it.unit,
            productPrice: up, unitType: getUnitType(found?.containerUnit||it.unit),
            quantity: it.quantity, unit: it.unit,
            unitCost: it.unitCost, totalCost: it.totalCost,
            isMain: it.isMain === 1 || it.isMain === true
          };
        });

        setFormData({
          name: d.name,
          yieldQuantity: d.yieldQuantity||'',
          yieldUnit: isCustom ? '__custom__' : loadedUnit,
          showInPhysicalInventory: d.showInPhysicalInventory === 0 || d.showInPhysicalInventory === false ? false : true,
          instructions: d.instructions || '',
          toPrepareQty: (d.toPrepareQty !== null && d.toPrepareQty !== undefined && d.toPrepareQty !== '') ? String(parseFloat(d.toPrepareQty)) : '',
          items
        });
        if (isCustom) setCustomYieldUnit(loadedUnit);
        else setCustomYieldUnit('');
        setEditingId(d.id);
        setShowDetailModal(false);
        setShowModal(true);
      }
    } catch { alert('Error loading preparation details'); }
    finally { setLoading(false); }
  };

  // ── Add product ──────────────────────────────────────────────────────────
  const handleOpenProductModal = () => { setShowProductModal(true); setProductSearch(''); };
  const handleSelectProduct = (product) => {
    if (formData.items.find(i => i.itemType === 'product' && i.productId === product.id)) { alert('Already added'); return; }
    const up = getUnitPrice(product);
    const tc = calcIngCost(1, product.containerUnit, product.containerSize, product.containerUnit, up);
    setFormData(prev => ({ ...prev, items: [...prev.items, {
      itemType: 'product',
      productId: product.id, productName: product.name,
      productSize: product.containerSize, productUnit: product.containerUnit,
      productPrice: up, unitType: getUnitType(product.containerUnit),
      quantity: 1, unit: product.containerUnit,
      unitCost: tc.toFixed(2), totalCost: tc.toFixed(2), isMain: false
    }] }));
    setShowProductModal(false);
  };

  // ── Add sub-prep ──────────────────────────────────────────────────────────
  const handleOpenPrepModal = () => { setShowPrepModal(true); setPrepSearch(''); };
  const handleSelectPrep = (prep) => {
    if (editingId && parseInt(prep.id) === parseInt(editingId)) { alert('A preparation cannot reference itself'); return; }
    if (formData.items.find(i => i.itemType === 'prep' && i.prepId === prep.id)) { alert('Already added'); return; }
    const bq = prep.yieldQuantity ? parseFloat(prep.yieldQuantity) : 1;
    const bu = prep.yieldUnit || 'Each';
    const bc = parseFloat(prep.totalCost) || 0;
    setFormData(prev => ({ ...prev, items: [...prev.items, {
      itemType: 'prep',
      prepId: prep.id, prepName: prep.name, prepCost: bc,
      baseQuantity: bq, baseUnit: bu, quantity: 1, unit: bu,
      unitType: getUnitType(bu), totalCost: (bc/bq).toFixed(2), isMain: false
    }] }));
    setShowPrepModal(false);
  };

  // ── Add section ──────────────────────────────────────────────────────────
  const handleAddSection = () => {
    setFormData(prev => ({ ...prev, items: [...prev.items, { itemType: 'section', sectionLabel: '' }] }));
  };
  const handleSectionLabelChange = (index, value) => {
    setFormData(prev => {
      const list = [...prev.items];
      list[index] = { ...list[index], sectionLabel: value };
      return { ...prev, items: list };
    });
  };

  // ── Cambiar cantidad/unidad de un item (recalcula costo según tipo) ──
  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const list = [...prev.items];
      const it = { ...list[index], [field]: value };
      if (field === 'quantity' || field === 'unit') {
        if (it.itemType === 'product') {
          const qty = parseFloat(it.quantity)||0;
          const tc  = calcIngCost(qty, it.unit, it.productSize, it.productUnit, it.productPrice);
          it.totalCost = tc.toFixed(2);
          it.unitCost  = qty > 0 ? (tc/qty).toFixed(2) : '0.00';
        } else if (it.itemType === 'prep') {
          it.totalCost = calcPrepCostFn(it, parseFloat(it.quantity)||0, it.unit||'Each');
        }
      }
      list[index] = it;
      return { ...prev, items: list };
    });
  };

  const handleRemoveItem = (i) => setFormData(prev => ({ ...prev, items: prev.items.filter((_,idx) => idx !== i) }));

  // ── Reorder: drag & drop (desktop) ──────────────────────────────────────
  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null) return;
    if (index !== dragOverIndex) setDragOverIndex(index);
  };
  const handleDrop = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) { setDragIndex(null); setDragOverIndex(null); return; }
    setFormData(prev => {
      const list = [...prev.items];
      const [moved] = list.splice(dragIndex, 1);
      list.splice(index, 0, moved);
      return { ...prev, items: list };
    });
    setDragIndex(null);
    setDragOverIndex(null);
  };
  const handleDragEnd = () => { setDragIndex(null); setDragOverIndex(null); };

  // ── Reorder: flechas ↑/↓ (mobile) ───────────────────────────────────────
  const handleMoveItem = (index, direction) => {
    setFormData(prev => {
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      const list = [...prev.items];
      if (newIndex < 0 || newIndex >= list.length) return prev;
      [list[index], list[newIndex]] = [list[newIndex], list[index]];
      return { ...prev, items: list };
    });
  };

  // ── Save ────────────────────────────────────────────────────────────────
  const handleSavePrep = async () => {
    if (!selectedStore)        { alert('Please select a store'); return; }
    if (!formData.name.trim()) { alert('Preparation name is required'); return; }
    if (realItemsCount() === 0) {
      alert('At least one ingredient or sub-preparation is required'); return;
    }
    try {
      setLoading(true);
      const resolvedYieldUnit = formData.yieldUnit === '__custom__'
        ? (customYieldUnit.trim() || null)
        : (formData.yieldUnit || null);

      // Lista unificada ordenada (el índice global ES el display_order)
      const items = formData.items.map(it => {
        if (it.itemType === 'section') {
          return { itemType: 'section', sectionLabel: (it.sectionLabel || '').trim() };
        }
        if (it.itemType === 'prep') {
          return {
            itemType: 'prep',
            prepId: it.prepId, prepName: it.prepName,
            quantity: parseFloat(it.quantity), unit: it.unit || it.baseUnit || 'Each',
            baseUnit: it.baseUnit, totalCost: parseFloat(it.totalCost),
            isMain: it.isMain ? true : false
          };
        }
        return {
          itemType: 'product',
          productId: it.productId, quantity: parseFloat(it.quantity),
          unit: it.unit, unitCost: parseFloat(it.unitCost),
          totalCost: parseFloat(it.totalCost), isMain: it.isMain ? true : false
        };
      });

      // Compat con backend viejo: derivar ingredients (product+section) y subPreps
      const ingredients = items.filter(i => i.itemType === 'product' || i.itemType === 'section');
      const subPreps    = items.filter(i => i.itemType === 'prep');

      const data = {
        storeId: selectedStore.id, name: formData.name.trim(),
        yieldQuantity: formData.yieldQuantity ? parseFloat(formData.yieldQuantity) : null,
        yieldUnit: resolvedYieldUnit,
        showInPhysicalInventory: formData.showInPhysicalInventory !== false,
        instructions: formData.instructions?.trim() || null,
        toPrepareQty: formData.toPrepareQty ? parseFloat(formData.toPrepareQty) : null,
        items, ingredients, subPreps
      };
      const r = editingId ? await updatePrep(editingId, data) : await createPrep(data);
      if (r.success) {
        alert(editingId ? 'Preparation updated successfully' : 'Preparation created successfully');
        await fetchPreps();
        handleCloseModal();
      }
    } catch (err) {
      const msg = err.response?.data?.message || '';
      if      (err.response?.status === 409) alert('A preparation with that name already exists in this store');
      else if (msg.includes('Circular') || msg.includes('itself')) alert(msg);
      else alert('Error saving preparation');
    } finally { setLoading(false); }
  };

  // ── Delete ──────────────────────────────────────────────────────────────
  const handleOpenDeleteModal  = (id) => { if (kitchenMode) return; setDeleteId(id); setShowDeleteModal(true); };
  const handleCloseDeleteModal = () => { setShowDeleteModal(false); setDeleteId(null); };
  const handleConfirmDelete    = async () => {
    try {
      setLoading(true);
      const r = await deletePrep(deleteId);
      if (r.success) {
        alert('Preparation deleted successfully');
        await fetchPreps();
        handleCloseDeleteModal();
        handleCloseDetail();
      }
    } catch { alert('Error deleting preparation'); }
    finally { setLoading(false); }
  };

  const filteredPreps = preps.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
    if (kitchenMode) return matchesSearch && !!p.mainIngredientName;
    return matchesSearch;
  });
  const filteredProducts       = products.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()) || (p.productCode && p.productCode.toLowerCase().includes(productSearch.toLowerCase())));
  const filteredPrepsForPicker = preps.filter(p => p.name.toLowerCase().includes(prepSearch.toLowerCase()) && (!editingId || parseInt(p.id) !== parseInt(editingId)));

  // ── Unit selects ─────────────────────────────────────────────────────────
  const IngUnitSelect = ({ unitType, value, onChange }) => (
    <select value={value} onChange={onChange} disabled={loading} className="pr-input-md">
      <option value="">Select…</option>
      {unitType === 'volume' && <optgroup label="Volume"><option value="ml">ml</option><option value="L">L (liter)</option><option value="Gallon">Gallon</option><option value="fl oz">fl oz</option></optgroup>}
      {unitType === 'weight' && <optgroup label="Weight"><option value="g">g (gram)</option><option value="kg">kg (kilogram)</option><option value="lb">lb (pound)</option><option value="oz">oz (ounce)</option></optgroup>}
      {(unitType === 'count' || !unitType) && <optgroup label="Other"><option value="Each">Each</option></optgroup>}
    </select>
  );

  const KNOWN_UNITS_SET = new Set(['ml','L','L (liter)','Gallon','fl oz','g','g (gram)','kg','kg (kilogram)','lb','lb (pound)','oz','oz (ounce)','Each']);
  const SubPrepUnitSelect = ({ sp, value, onChange }) => {
    const isCustomBase = sp.baseUnit && !KNOWN_UNITS_SET.has(sp.baseUnit);
    return (
      <select value={value||sp.baseUnit||'Each'} onChange={onChange} disabled={loading} className="pr-input-md">
        <option value="">Select unit…</option>
        {isCustomBase && (
          <optgroup label="Custom">
            <option value={sp.baseUnit}>{sp.baseUnit}</option>
          </optgroup>
        )}
        {!isCustomBase && (sp.unitType === 'count' || !sp.unitType || sp.baseUnit === 'Each') && <optgroup label="Standard"><option value="Each">Each (batch)</option></optgroup>}
        {sp.unitType === 'volume' && <optgroup label="Volume"><option value="ml">ml</option><option value="L">L (liter)</option><option value="Gallon">Gallon</option><option value="fl oz">fl oz</option></optgroup>}
        {sp.unitType === 'weight' && <optgroup label="Weight"><option value="g">g (gram)</option><option value="kg">kg (kilogram)</option><option value="lb">lb (pound)</option><option value="oz">oz (ounce)</option></optgroup>}
      </select>
    );
  };

  // ── Render de una fila del cuerpo de la tabla desktop (unificada) ──
  const renderDesktopRow = (it, i) => {
    const dragCls = [
      dragIndex === i ? 'pr-row-dragging' : '',
      dragOverIndex === i && dragIndex !== i ? 'pr-row-drag-over' : ''
    ].filter(Boolean).join(' ');

    const dragProps = {
      draggable: true,
      onDragStart: (e) => handleDragStart(e, i),
      onDragOver:  (e) => handleDragOver(e, i),
      onDrop:      (e) => handleDrop(e, i),
      onDragEnd:   handleDragEnd
    };

    if (it.itemType === 'section') {
      return (
        <tr key={i} className={['pr-section-row', dragCls].filter(Boolean).join(' ')} {...dragProps}>
          <td className="pr-drag-handle-cell" title="Drag to reorder"><FaGripVertical className="pr-drag-handle-icon" /></td>
          <td colSpan="4">
            <input
              type="text"
              className="pr-section-input"
              placeholder="Section title, e.g. Cooked, Raw…"
              value={it.sectionLabel}
              onChange={(e) => handleSectionLabelChange(i, e.target.value)}
              disabled={loading}
            />
          </td>
          <td><button className="pr-btn-remove" onClick={() => handleRemoveItem(i)} disabled={loading} type="button"><FaTrash /></button></td>
        </tr>
      );
    }

    if (it.itemType === 'prep') {
      return (
        <tr key={i} className={[it.isMain ? 'pr-row-main pr-row-main--green' : '', dragCls].filter(Boolean).join(' ')} {...dragProps}>
          <td className="pr-drag-handle-cell" title="Drag to reorder"><FaGripVertical className="pr-drag-handle-icon" /></td>
          <td>
            <button type="button" className={`pr-btn-star pr-btn-star--green ${it.isMain ? 'pr-btn-star--active-green' : ''}`} onClick={() => handleSetMain(i)} title={it.isMain ? 'Remove as main' : 'Set as main'}><FaStar /></button>
          </td>
          <td>
            <div className="pr-prep-info">
              <strong>{it.prepName} <span className="pr-detail-ing-type-badge">sub-prep</span></strong>
              <small>{it.baseQuantity > 1 && it.baseUnit !== 'Each' ? `$${(it.prepCost/it.baseQuantity).toFixed(4)} / ${it.baseUnit}` : `$${parseFloat(it.prepCost).toFixed(2)} / batch`}</small>
            </div>
          </td>
          <td><input type="number" step="1" min="1" value={it.quantity} onChange={e => handleItemChange(i, 'quantity', e.target.value)} disabled={loading} className="pr-input-sm" /></td>
          <td><SubPrepUnitSelect sp={it} value={it.unit} onChange={e => handleItemChange(i, 'unit', e.target.value)} /></td>
          <td className="pr-total-cost-cell">${parseFloat(it.totalCost).toFixed(2)}</td>
          <td><button className="pr-btn-remove" onClick={() => handleRemoveItem(i)} disabled={loading} type="button"><FaTrash /></button></td>
        </tr>
      );
    }

    // product
    return (
      <tr key={i} className={[it.isMain ? 'pr-row-main' : '', dragCls].filter(Boolean).join(' ')} {...dragProps}>
        <td className="pr-drag-handle-cell" title="Drag to reorder"><FaGripVertical className="pr-drag-handle-icon" /></td>
        <td>
          <button type="button" className={`pr-btn-star ${it.isMain ? 'pr-btn-star--active' : ''}`} onClick={() => handleSetMain(i)} title={it.isMain ? 'Remove as main' : 'Set as main'}><FaStar /></button>
        </td>
        <td>
          <div className="pr-product-info">
            <strong>{it.productName}</strong>
            <small>Unit price: ${parseFloat(it.productPrice).toFixed(4)} / {it.productUnit}</small>
          </div>
        </td>
        <td><input type="number" step="1" min="0" value={it.quantity} onChange={e => handleItemChange(i, 'quantity', e.target.value)} disabled={loading} className="pr-input-sm" /></td>
        <td><IngUnitSelect unitType={it.unitType} value={it.unit} onChange={e => handleItemChange(i, 'unit', e.target.value)} /></td>
        <td className="pr-cost-cell">${parseFloat(it.totalCost).toFixed(2)}</td>
        <td><button className="pr-btn-remove" onClick={() => handleRemoveItem(i)} disabled={loading} type="button"><FaTrash /></button></td>
      </tr>
    );
  };

  // ── Render de una card del cuerpo mobile (unificada) ──
  const renderMobileCard = (it, i) => {
    const orderBtns = (
      <div className="pr-ing-card-order">
        <button type="button" className="pr-btn-order" onClick={() => handleMoveItem(i, 'up')} disabled={i === 0 || loading}><FaArrowUp /></button>
        <button type="button" className="pr-btn-order" onClick={() => handleMoveItem(i, 'down')} disabled={i === formData.items.length - 1 || loading}><FaArrowDown /></button>
      </div>
    );

    if (it.itemType === 'section') {
      return (
        <div key={i} className="pr-section-card">
          <input
            type="text"
            className="pr-section-input pr-section-input--mobile"
            placeholder="Section title, e.g. Cooked, Raw…"
            value={it.sectionLabel}
            onChange={(e) => handleSectionLabelChange(i, e.target.value)}
            disabled={loading}
          />
          <div className="pr-section-card-actions">
            {orderBtns}
            <button className="pr-btn-remove" onClick={() => handleRemoveItem(i)} disabled={loading} type="button"><FaTrash /></button>
          </div>
        </div>
      );
    }

    if (it.itemType === 'prep') {
      return (
        <div key={i} className={`pr-ing-card pr-ing-card--prep ${it.isMain ? 'pr-ing-card--main-green' : ''}`}>
          <div className="pr-ing-card-header">
            <button type="button" className={`pr-btn-star pr-btn-star--green ${it.isMain ? 'pr-btn-star--active-green' : ''}`} onClick={() => handleSetMain(i)}><FaStar /></button>
            <div className="pr-ing-card-name">{it.prepName} <span className="pr-detail-ing-type-badge">sub-prep</span></div>
            {orderBtns}
            <button className="pr-btn-remove" onClick={() => handleRemoveItem(i)} disabled={loading} type="button"><FaTrash /></button>
          </div>
          <div className="pr-ing-card-meta">{it.baseQuantity > 1 && it.baseUnit !== 'Each' ? `$${(it.prepCost/it.baseQuantity).toFixed(4)} / ${it.baseUnit}` : `$${parseFloat(it.prepCost).toFixed(2)} / batch`}</div>
          <div className="pr-ing-card-fields">
            <div className="pr-ing-card-field">
              <label className="pr-ing-card-label">Quantity</label>
              <input type="number" step="1" min="1" value={it.quantity} onChange={e => handleItemChange(i, 'quantity', e.target.value)} disabled={loading} className="pr-ing-card-input" />
            </div>
            <div className="pr-ing-card-field">
              <label className="pr-ing-card-label">Unit</label>
              <SubPrepUnitSelect sp={it} value={it.unit} onChange={e => handleItemChange(i, 'unit', e.target.value)} />
            </div>
            <div className="pr-ing-card-field">
              <label className="pr-ing-card-label">Total Cost</label>
              <span className="pr-ing-card-cost pr-ing-card-cost--prep">${parseFloat(it.totalCost).toFixed(2)}</span>
            </div>
          </div>
        </div>
      );
    }

    // product
    return (
      <div key={i} className={`pr-ing-card ${it.isMain ? 'pr-ing-card--main' : ''}`}>
        <div className="pr-ing-card-header">
          <button type="button" className={`pr-btn-star ${it.isMain ? 'pr-btn-star--active' : ''}`} onClick={() => handleSetMain(i)}><FaStar /></button>
          <div className="pr-ing-card-name">{it.productName}</div>
          {orderBtns}
          <button className="pr-btn-remove" onClick={() => handleRemoveItem(i)} disabled={loading} type="button"><FaTrash /></button>
        </div>
        <div className="pr-ing-card-meta">Unit price: ${parseFloat(it.productPrice).toFixed(4)} / {it.productUnit}</div>
        <div className="pr-ing-card-fields">
          <div className="pr-ing-card-field">
            <label className="pr-ing-card-label">Quantity</label>
            <input type="number" step="1" min="0" value={it.quantity} onChange={e => handleItemChange(i, 'quantity', e.target.value)} disabled={loading} className="pr-ing-card-input" />
          </div>
          <div className="pr-ing-card-field">
            <label className="pr-ing-card-label">Unit</label>
            <IngUnitSelect unitType={it.unitType} value={it.unit} onChange={e => handleItemChange(i, 'unit', e.target.value)} />
          </div>
          <div className="pr-ing-card-field">
            <label className="pr-ing-card-label">Cost</label>
            <span className="pr-ing-card-cost">${parseFloat(it.totalCost).toFixed(2)}</span>
          </div>
        </div>
      </div>
    );
  };

  if (!selectedStore) {
    return (
      <div className="pr-page">
        <div className="pr-page-header"><h1 className="pr-page-title">Pre-Batch</h1></div>
        <div className="pr-no-store-msg"><p>Please select a store from the sidebar to view preparations</p></div>
      </div>
    );
  }

  return (
    <div className="pr-page">

      {/* ── HEADER ── */}
      <div className="pr-page-header pr-sticky-header">
        <div className="pr-header-left">
          <h1 className="pr-page-title">Pre-Batch</h1>
          {selectedStore && <span className="pr-store-badge">{selectedStore.name}</span>}
        </div>
        {!kitchenMode && (
          <button className="pr-btn-primary" onClick={handleOpenModal} disabled={loading}>
            <FaPlus /> Create new preparation
          </button>
        )}
      </div>

      {error && <div className="pr-error-msg">{error}<button onClick={fetchPreps}>Retry</button></div>}

      {/* ── SEARCH ── */}
      <div className="pr-search-section">
        <div className="pr-search-bar">
          <input type="text" placeholder="Search by name…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
      </div>

      {/* ══ TABLE desktop ══ */}
      <div className="pr-table-wrap pr-desktop-only">
        {loading && <div className="pr-loading">Loading…</div>}
        <table className="pr-data-table">
          <thead>
            <tr>
              <th>Preparation Name</th>
              <th>Ingredients</th>
              {!kitchenMode && <th>Total Cost</th>}
              {!kitchenMode && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredPreps.length === 0 ? (
              <tr><td colSpan={kitchenMode ? 2 : 4} style={{ textAlign:'center', padding:'40px', color:'#94a3b8' }}>{loading ? 'Loading…' : 'No results found'}</td></tr>
            ) : filteredPreps.map(prep => (
              <tr key={prep.id}>
                <td className="pr-prep-name-cell" onClick={() => handleOpenDetail(prep)}>
                  <span className="pr-prep-name-link">
                    {prep.name}
                    <FaChevronRight className="pr-prep-name-arrow" />
                  </span>
                  <span className="pr-prep-name-hint">{kitchenMode ? 'Tap to view & scale' : 'Click to view & scale ingredients'}</span>
                  <span className="pr-badges-row">
                    {prep.mainIngredientName && (
                      <span className="pr-main-badge"><FaStar className="pr-main-badge-icon" /> {prep.mainIngredientName}</span>
                    )}
                    {!kitchenMode && (prep.showInPhysicalInventory === 0 || prep.showInPhysicalInventory === false) && (
                      <span className="pr-hidden-pi-badge"><FaEyeSlash className="pr-hidden-pi-badge-icon" /> Not counted in Physical Inv.</span>
                    )}
                  </span>
                </td>
                <td>{prep.ingredientCount} ingredient(s)</td>
                {!kitchenMode && <td style={{ fontWeight:600 }}>${parseFloat(prep.totalCost).toFixed(2)}</td>}
                {!kitchenMode && (
                  <td>
                    <button className="pr-btn-action" onClick={() => handleEditModal(prep)} disabled={loading}>Edit</button>
                    <button className="pr-btn-action pr-btn-delete" onClick={() => handleOpenDeleteModal(prep.id)} disabled={loading}>Delete</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ══ CARDS mobile ══ */}
      <div className="pr-list-cards pr-mobile-only">
        {loading && <div className="pr-loading">Loading…</div>}
        {!loading && filteredPreps.length === 0 && <div className="pr-loading">No results found</div>}
        {filteredPreps.map(prep => (
          <div key={prep.id} className="pr-list-card" onClick={() => handleOpenDetail(prep)}>
            <div className="pr-list-card-top">
              <div className="pr-list-card-name-row">
                <span className="pr-list-card-name pr-list-card-name--link">{prep.name}</span>
                <FaChevronRight className="pr-list-card-name-arrow" />
              </div>
              <span className="pr-list-card-name-hint">Tap to view &amp; scale</span>
              <span className="pr-badges-row">
                {prep.mainIngredientName && (
                  <span className="pr-main-badge"><FaStar className="pr-main-badge-icon" /> {prep.mainIngredientName}</span>
                )}
                {!kitchenMode && (prep.showInPhysicalInventory === 0 || prep.showInPhysicalInventory === false) && (
                  <span className="pr-hidden-pi-badge"><FaEyeSlash className="pr-hidden-pi-badge-icon" /> Not counted in Physical Inv.</span>
                )}
              </span>
            </div>
            <div className="pr-list-card-stats">
              <div className={`pr-list-card-stat ${kitchenMode ? 'pr-list-card-stat--full' : ''}`}>
                <span className="pr-list-card-stat-label">Ingredients</span>
                <span className="pr-list-card-stat-value">{prep.ingredientCount}</span>
              </div>
              {!kitchenMode && (
                <div className="pr-list-card-stat">
                  <span className="pr-list-card-stat-label">Total Cost</span>
                  <span className="pr-list-card-stat-value pr-list-card-stat-cost">${parseFloat(prep.totalCost).toFixed(2)}</span>
                </div>
              )}
            </div>
            {!kitchenMode && (
              <div className="pr-list-card-footer" onClick={e => e.stopPropagation()}>
                <button className="pr-list-card-btn pr-list-card-btn--edit" onClick={() => handleEditModal(prep)} disabled={loading}>Edit</button>
                <button className="pr-list-card-btn pr-list-card-btn--delete" onClick={() => handleOpenDeleteModal(prep.id)} disabled={loading}><FaTrash /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          MODAL DETALLE con escalado
          ══════════════════════════════════════════════════════════════════ */}
      {showDetailModal && detailPrep && (
        <ModalPortal>
          <div className="pr-modal-overlay--detail">
            <div className="pr-modal pr-modal-detail" onClick={e => e.stopPropagation()}>

              {/* ── Header ── */}
              <div className="pr-detail-modal-header">
                <div className="pr-detail-modal-header-row">
                  <button className="pr-detail-modal-back" onClick={handleCloseDetail} title="Back">←</button>
                  <div className="pr-detail-modal-title-block">
                    <h2 className="pr-detail-modal-title">{detailPrep.name}</h2>
                  </div>
                  <div className="pr-detail-modal-actions">
                    <button className="pr-btn-icon" onClick={handlePrintRawBT} title="Print with RawBT (mobile)">
                      <FaMobileAlt />
                    </button>
                    {!kitchenMode && (
                      <>
                        <button className="pr-btn-icon" onClick={() => handleEditModal(detailPrep)} disabled={loading} title="Edit">
                          <FaEdit />
                        </button>
                        <button className="pr-btn-icon pr-btn-icon--danger" onClick={() => handleOpenDeleteModal(detailPrep.id)} disabled={loading} title="Delete">
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="pr-modal-body">

                {/* ── Panel de escala ── */}
                {detailMainItem ? (
                  <div className={`pr-scale-panel ${isScaled ? 'pr-scale-panel--active' : ''}`}>
                    <div className="pr-scale-panel-header">
                      <FaStar className="pr-scale-star-icon" />
                      <span className="pr-scale-label">Scale by main ingredient</span>
                      {isScaled && <span className="pr-scale-badge">{scale.toFixed(2)}×</span>}
                    </div>
                    <div className="pr-scale-row">
                      <span className="pr-scale-ing-name">
                        {detailMainItem.productName || detailMainItem.prepName}
                      </span>
                      <div className="pr-scale-input-wrap" data-qty={`${mainQty} ${detailMainItem.unit}`}>
                        <input
                          type="number"
                          min="0.01"
                          step="any"
                          value={mainQty}
                          onChange={e => setMainQty(e.target.value)}
                          className={`pr-scale-input ${isScaled ? 'pr-scale-input--active' : ''}`}
                        />
                        <span className="pr-scale-print-value">{mainQty}</span>
                        <span className="pr-scale-unit">{detailMainItem.unit}</span>
                      </div>
                      {isScaled && (
                        <button className="pr-btn-reset" onClick={() => setMainQty(String(origQty))} title="Reset">
                          <FaUndo />
                        </button>
                      )}
                    </div>
                    <div className="pr-scale-original">Original: {origQty} {detailMainItem.unit}</div>
                  </div>
                ) : (
                  <div className="pr-scale-panel pr-scale-panel--empty">
                    <FaStar className="pr-scale-star-icon pr-scale-star-icon--empty" />
                    <span className="pr-scale-label--empty">No main ingredient — edit this prep to enable scaling</span>
                  </div>
                )}

                {/* ── Lista unificada (products + sections + sub-preps) ── */}
                <div className="pr-detail-ingredients">
                  <div className="pr-detail-section-label">ALL INGREDIENTS</div>

                  {detailItems.map((it, i) => {
                    if (it.itemType === 'section') {
                      return (
                        <div key={`sec-${i}`} className="pr-detail-section-divider">
                          {it.sectionLabel}
                        </div>
                      );
                    }
                    const isMain  = it.isMain === 1 || it.isMain === true;
                    const display = scaledQty(parseFloat(it.quantity), isMain);
                    const name    = it.itemType === 'prep' ? it.prepName : it.productName;
                    return (
                      <div key={`it-${i}`} className={`pr-detail-ing-row ${isMain ? 'pr-detail-ing-row--main' : ''}`}>
                        <div className="pr-detail-ing-left">
                          {isMain
                            ? <FaStar className="pr-detail-star" />
                            : <span className="pr-detail-star-placeholder" />}
                          <span className="pr-detail-ing-name">{name}</span>
                          {it.itemType === 'prep' && <span className="pr-detail-ing-type-badge">sub-prep</span>}
                        </div>
                        <div className="pr-detail-ing-right">
                          <span className={`pr-detail-qty ${isScaled && !isMain ? 'pr-detail-qty--scaled' : ''}`}>
                            {display}
                          </span>
                          <span className="pr-detail-unit">{it.unit}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* ── Instrucciones (visible para todos, incluyendo modo cocina) ── */}
                {detailPrep.instructions && detailPrep.instructions.trim() && (
                  <div className="pr-detail-instructions">
                    <div className="pr-detail-section-label">INSTRUCTIONS</div>
                    <div className="pr-detail-instructions-text">{detailPrep.instructions}</div>
                  </div>
                )}

                {/* ── Costos (ocultos en modo kitchen) ── */}
                {!kitchenMode && (
                  <div className="pr-detail-costs">
                    <div className="pr-detail-cost-row">
                      <span className="pr-detail-cost-label">
                        {isScaled ? 'Original batch cost' : 'Total batch cost'}
                      </span>
                      <span className="pr-detail-cost-value">
                        ${parseFloat(detailPrep.totalCost).toFixed(2)}
                      </span>
                    </div>
                    {isScaled && (
                      <div className="pr-detail-cost-row pr-detail-cost-row--scaled">
                        <span className="pr-detail-cost-label">
                          Scaled cost <span className="pr-detail-cost-scale-tag">{scale.toFixed(2)}×</span>
                        </span>
                        <span className="pr-detail-cost-value pr-detail-cost-value--scaled">
                          ${(parseFloat(detailPrep.totalCost) * scale).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {detailPrep.yieldQuantity && detailPrep.yieldUnit && (
                      <div className="pr-detail-cost-row pr-detail-cost-row--yield">
                        <span className="pr-detail-cost-label">
                          Cost per {detailPrep.yieldUnit}
                        </span>
                        <span className="pr-detail-cost-value">
                          ${(parseFloat(detailPrep.totalCost) / parseFloat(detailPrep.yieldQuantity)).toFixed(6)} / {detailPrep.yieldUnit}
                        </span>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      {/* ══ MODAL CREATE/EDIT ══ */}
      {showModal && (
        <ModalPortal>
          <div className="pr-modal-overlay">
            <div className="pr-modal pr-modal-lg" onClick={e => e.stopPropagation()}>
              <div className="pr-modal-header">
                <h2>{editingId ? 'Edit preparation' : 'Create new preparation'}</h2>
                <button className="pr-btn-close" onClick={handleCloseModal}><FaTimes /></button>
              </div>
              <div className="pr-modal-body">

                <div className="pr-name-row">
                  <div className="pr-form-group pr-name-field">
                    <label>Preparation Name *</label>
                    <input type="text" value={formData.name} onChange={e => handleInput('name', e.target.value)} placeholder="e.g., House BBQ Sauce" disabled={loading} />
                  </div>
                  <div className="pr-form-group pr-physical-inv-field">
                    <label>Physical Inventory</label>
                    <label className="pr-toggle-switch">
                      <input
                        type="checkbox"
                        checked={formData.showInPhysicalInventory}
                        onChange={e => handleInput('showInPhysicalInventory', e.target.checked)}
                        disabled={loading}
                      />
                      <span className="pr-toggle-slider"></span>
                    </label>
                    <span className="pr-toggle-hint">
                      {formData.showInPhysicalInventory ? 'Counted in Physical Inventory' : 'Hidden from Physical Inventory'}
                    </span>
                  </div>
                </div>

                {/* ── ITEMS (tabla ÚNICA: productos + secciones + sub-preps) ── */}
                <div className="pr-ingredients-section">
                  <div className="pr-section-header">
                    <h3>Ingredients &amp; Pre-Batch</h3>
                  </div>
                  <p className="pr-star-hint"><FaStar className="pr-star-hint-icon" /> Tap the star to mark the main item for scaling</p>
                  <p className="pr-star-hint pr-drag-hint pr-drag-hint--desktop"><FaGripVertical className="pr-star-hint-icon" /> Drag rows by the handle to reorder</p>
                  <p className="pr-star-hint pr-drag-hint pr-drag-hint--mobile"><FaArrowUp className="pr-star-hint-icon" /> Use the arrows to reorder</p>

                  {formData.items.length === 0
                    ? <div className="pr-empty-state">No items added yet.</div>
                    : (
                      <>
                        <div className="pr-sub-table-wrap pr-desktop-only">
                          <table className="pr-sub-table pr-ing-table">
                            <thead><tr><th className="pr-th-drag"></th><th className="pr-th-star">★</th><th>Item</th><th>Quantity</th><th>Unit</th><th>Cost</th><th></th></tr></thead>
                            <tbody>
                              {formData.items.map((it, i) => renderDesktopRow(it, i))}
                            </tbody>
                            <tfoot>
                              <tr className="pr-total-row">
                                <td colSpan="5" className="pr-total-label">Total:</td>
                                <td className="pr-total-cost">${totalCost()}</td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                        <div className="pr-ing-cards pr-mobile-only">
                          {formData.items.map((it, i) => renderMobileCard(it, i))}
                          <div className="pr-ing-subtotal">Total: <strong>${totalCost()}</strong></div>
                        </div>
                      </>
                    )}

                  {/* ── Botones: Section / Ingredient / Sub-Prep (todos a la misma lista) ── */}
                  <div className="pr-section-add-btn-wrap">
                    <button className="pr-btn-add-section" onClick={handleAddSection} disabled={loading} type="button">
                      <FaHeading /> Add Section
                    </button>
                    <button className="pr-btn-add" onClick={handleOpenProductModal} disabled={loading} type="button">
                      <FaPlus /> Add Ingredient
                    </button>
                    <button className="pr-btn-add pr-btn-add-green" onClick={handleOpenPrepModal} disabled={loading} type="button">
                      <FaPlus /> Add Sub-Prep
                    </button>
                  </div>
                </div>

                {/* ── INSTRUCTIONS ── */}
                <div className="pr-instructions-section">
                  <div className="pr-section-header"><h3><FaClipboardList style={{ fontSize: 15, marginRight: 4 }} /> Instructions (Optional)</h3></div>
                  <p className="pr-instructions-hint">Write the step-by-step procedure for this preparation. This will be visible to kitchen staff and included when printing.</p>
                  <textarea
                    className="pr-instructions-textarea"
                    placeholder="e.g.&#10;1. Combine all dry ingredients in a large bowl.&#10;2. Slowly whisk in the wet ingredients until smooth.&#10;3. Simmer on low heat for 20 minutes, stirring occasionally."
                    value={formData.instructions}
                    onChange={e => handleInput('instructions', e.target.value)}
                    disabled={loading}
                    rows={8}
                  />
                </div>

                {/* ── YIELD ── */}
                <div className="pr-yield-section">
                  <div className="pr-section-header"><h3>Yield (Optional)</h3></div>
                  <div className="pr-form-row pr-form-row-3">
                    <div className="pr-form-group">
                      <label>Yield Quantity</label>
                      <input type="number" step="1" min="0" value={formData.yieldQuantity} onChange={e => handleInput('yieldQuantity', e.target.value)} placeholder="e.g. 2000" disabled={loading} />
                    </div>
                    <div className="pr-form-group">
                      <label>Yield Unit</label>
                      {formData.yieldUnit === '__custom__' ? (
                        <div className="pr-custom-unit-wrap">
                          <input
                            type="text"
                            className="pr-custom-unit-input"
                            placeholder="e.g. portions, shots, cups…"
                            value={customYieldUnit}
                            onChange={e => setCustomYieldUnit(e.target.value)}
                            disabled={loading}
                            autoFocus
                          />
                          <button
                            type="button"
                            className="pr-custom-unit-back"
                            onClick={() => { handleInput('yieldUnit', ''); setCustomYieldUnit(''); }}
                            title="Back to list"
                            disabled={loading}
                          >↩</button>
                        </div>
                      ) : (
                        <select
                          value={formData.yieldUnit}
                          onChange={e => handleInput('yieldUnit', e.target.value)}
                          disabled={loading}
                        >
                          <option value="">Select…</option>
                          <optgroup label="Volume">
                            <option value="ml">ml</option>
                            <option value="L">L (liter)</option>
                            <option value="Gallon">Gallon</option>
                            <option value="fl oz">fl oz</option>
                          </optgroup>
                          <optgroup label="Weight">
                            <option value="g">g (gram)</option>
                            <option value="kg">kg (kilogram)</option>
                            <option value="lb">lb (pound)</option>
                            <option value="oz">oz (ounce)</option>
                          </optgroup>
                          <optgroup label="Other">
                            <option value="Each">Each</option>
                            <option value="__custom__">Custom…</option>
                          </optgroup>
                        </select>
                      )}
                    </div>
                    <div className="pr-form-group">
                      <label>Unit Cost</label>
                      <input type="text" readOnly className="pr-input-readonly"
                        value={(() => {
                          const displayUnit = formData.yieldUnit === '__custom__'
                            ? (customYieldUnit.trim() || 'unit')
                            : (formData.yieldUnit || 'unit');
                          return formData.yieldQuantity && parseFloat(formData.yieldQuantity) > 0
                            ? `$${(parseFloat(totalCost()) / parseFloat(formData.yieldQuantity)).toFixed(6)} / ${displayUnit}`
                            : '—';
                        })()} />
                    </div>
                  </div>
                </div>

                {/* ── TO PREPARE ── */}
                <div className="pr-toprepare-section pr-yield-section">
                  <div className="pr-section-header"><h3><FaStar style={{ fontSize: 14, marginRight: 4 }} /> To Prepare (Optional)</h3></div>
                  {(() => {
                    const mainItem = formData.items.find(it => (it.itemType === 'product' || it.itemType === 'prep') && it.isMain);
                    const mainName = mainItem ? (mainItem.itemType === 'prep' ? mainItem.prepName : mainItem.productName) : null;
                    const mainUnit = mainItem ? (mainItem.unit || '') : '';
                    return (
                      <div className="pr-form-row">
                        <div className="pr-form-group" style={{ flex: 1 }}>
                          <label>Main ingredient quantity {mainName ? `(${mainName})` : ''}</label>
                          <input
                            type="number" step="any" min="0"
                            value={formData.toPrepareQty}
                            onChange={e => handleInput('toPrepareQty', e.target.value)}
                            placeholder={mainItem ? `Default: ${mainItem.quantity} ${mainUnit}` : 'Select a main ingredient (star) first'}
                            disabled={loading || !mainItem}
                          />
                          <small style={{ color: '#64748b', fontSize: 12, display: 'block', marginTop: 4 }}>
                            {mainItem
                              ? `Quantity of the main ingredient the kitchen should prepare (in ${mainUnit}). The ingredient list opens scaled to this value.`
                              : 'Mark one ingredient as main (star) to enable this.'}
                          </small>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {formData.items.length > 0 && (
                  <div className="pr-final-total">
                    <div className="pr-final-total-inner">
                      <div>
                        <div className="pr-final-total-label">Total Preparation Cost</div>
                        <div className="pr-final-total-breakdown">Ingredients: ${totalIngCost()} + Sub-Preps: ${totalSubCost()}</div>
                      </div>
                      <div className="pr-final-total-value">${totalCost()}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="pr-modal-footer">
                <button className="pr-btn-secondary" onClick={handleCloseModal} disabled={loading}>Cancel</button>
                <button className="pr-btn-primary"   onClick={handleSavePrep}   disabled={loading}>{loading ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      {/* ══ MODAL SELECT PRODUCT ══ */}
      {showProductModal && (
        <ModalPortal>
          <div className="pr-modal-overlay" onClick={() => setShowProductModal(false)}>
            <div className="pr-modal pr-modal-picker" onClick={e => e.stopPropagation()}>
              <div className="pr-modal-header">
                <h2>Select Product</h2>
                <button className="pr-btn-close" onClick={() => setShowProductModal(false)}><FaTimes /></button>
              </div>
              <div className="pr-modal-body">
                <div className="pr-form-group">
                  <input type="text" placeholder="Search products…" value={productSearch} onChange={e => setProductSearch(e.target.value)} autoFocus />
                </div>
                <div className="pr-picker-list pr-desktop-only">
                  <table className="pr-picker-table">
                    <thead><tr><th>Select</th><th>Product Name</th><th>Category</th><th>Unit Price</th></tr></thead>
                    <tbody>
                      {filteredProducts.length === 0
                        ? <tr><td colSpan="4" style={{ textAlign:'center', padding:'20px', color:'#94a3b8' }}>No products found</td></tr>
                        : filteredProducts.map(p => (
                          <tr key={p.id}>
                            <td><button className="pr-btn-select" onClick={() => handleSelectProduct(p)}>✓</button></td>
                            <td>{p.name}</td><td>{p.categoryName}</td>
                            <td>${getUnitPrice(p).toFixed(4)} / {p.containerUnit}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                <div className="pr-picker-mobile-list pr-mobile-only">
                  {filteredProducts.length === 0
                    ? <div className="pr-loading">No products found</div>
                    : filteredProducts.map(p => (
                      <div key={p.id} className="pr-picker-mobile-item" onClick={() => handleSelectProduct(p)}>
                        <div className="pr-picker-mobile-name">{p.name}</div>
                        <div className="pr-picker-mobile-meta">{p.categoryName} · ${getUnitPrice(p).toFixed(4)} / {p.containerUnit}</div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      {/* ══ MODAL SELECT SUB-PREP ══ */}
      {showPrepModal && (
        <ModalPortal>
          <div className="pr-modal-overlay" onClick={() => setShowPrepModal(false)}>
            <div className="pr-modal pr-modal-picker" onClick={e => e.stopPropagation()}>
              <div className="pr-modal-header">
                <h2>Select Sub-Preparation</h2>
                <button className="pr-btn-close" onClick={() => setShowPrepModal(false)}><FaTimes /></button>
              </div>
              <div className="pr-modal-body">
                <div className="pr-form-group">
                  <input type="text" placeholder="Search preparations…" value={prepSearch} onChange={e => setPrepSearch(e.target.value)} autoFocus />
                </div>
                <div className="pr-picker-list pr-desktop-only">
                  <table className="pr-picker-table">
                    <thead><tr><th>Select</th><th>Preparation Name</th><th>Yield</th><th>Cost</th></tr></thead>
                    <tbody>
                      {filteredPrepsForPicker.length === 0
                        ? <tr><td colSpan="4" style={{ textAlign:'center', padding:'20px', color:'#94a3b8' }}>No preparations found</td></tr>
                        : filteredPrepsForPicker.map(p => (
                          <tr key={p.id}>
                            <td><button className="pr-btn-select" onClick={() => handleSelectPrep(p)}>✓</button></td>
                            <td>{p.name}</td>
                            <td>{p.yieldQuantity && p.yieldUnit ? `${p.yieldQuantity} ${p.yieldUnit}` : '—'}</td>
                            <td>${parseFloat(p.totalCost).toFixed(2)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                <div className="pr-picker-mobile-list pr-mobile-only">
                  {filteredPrepsForPicker.length === 0
                    ? <div className="pr-loading">No preparations found</div>
                    : filteredPrepsForPicker.map(p => (
                      <div key={p.id} className="pr-picker-mobile-item" onClick={() => handleSelectPrep(p)}>
                        <div className="pr-picker-mobile-name">{p.name}</div>
                        <div className="pr-picker-mobile-meta">{p.yieldQuantity && p.yieldUnit ? `${p.yieldQuantity} ${p.yieldUnit}` : '—'} · ${parseFloat(p.totalCost).toFixed(2)}</div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      {/* ══ MODAL DELETE ══ */}
      {showDeleteModal && (
        <ModalPortal>
          <div className="pr-modal-overlay" onClick={handleCloseDeleteModal}>
            <div className="pr-modal pr-modal-sm" onClick={e => e.stopPropagation()}>
              <div className="pr-modal-header">
                <h2>Confirm deletion</h2>
                <button className="pr-btn-close" onClick={handleCloseDeleteModal}><FaTimes /></button>
              </div>
              <div className="pr-modal-body">
                <p style={{ fontSize:14, color:'#374151', marginBottom:8 }}>Are you sure you want to delete this preparation?</p>
                <p className="pr-warning-text">This action cannot be undone.</p>
              </div>
              <div className="pr-modal-footer">
                <button className="pr-btn-secondary" onClick={handleCloseDeleteModal} disabled={loading}>Cancel</button>
                <button className="pr-btn-danger"    onClick={handleConfirmDelete}    disabled={loading}>{loading ? 'Deleting…' : 'Delete'}</button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
}

export default Preps;